/**
 * @brief MQTT 指令协议
 * @note 下行 topic: rtc-aigc/<roomId>/cmd
 *       上行 topic: rtc-aigc/<roomId>/state
 */

import { v4 as uuidv4 } from 'uuid';

export type AnswerMode = 'doubao-llm' | 'external-llm' | 'text-broadcast';

export type LlmProvider = 'openai' | 'anthropic' | 'openrouter' | 'deepseek' | 'doubao-ark';

export interface TtsVoicePayload {
  voice_type: string;
  speed_ratio?: number;
  pitch_ratio?: number;
  volume_ratio?: number;
}

export type CmdPayloadMap = {
  set_mode: { mode: AnswerMode };
  broadcast_text: { text: string; speakNow?: boolean };
  user_text: { text: string };
  switch_llm: {
    provider: LlmProvider;
    model: string;
    baseURL?: string;
    apiKey?: string;
    systemPrompt?: string;
  };
  switch_voice: TtsVoicePayload;
  interrupt: Record<string, never>;
  inject_history: { role: 'user' | 'assistant'; text: string };
};

export type CmdType = keyof CmdPayloadMap;

export interface CmdEnvelope<T extends CmdType = CmdType> {
  id: string;
  ts: number;
  type: T;
  payload: CmdPayloadMap[T];
}

const envelope = <T extends CmdType>(type: T, payload: CmdPayloadMap[T]): CmdEnvelope<T> => ({
  id: uuidv4(),
  ts: Date.now(),
  type,
  payload,
});

export const makeSetMode = (mode: AnswerMode) => envelope('set_mode', { mode });
export const makeBroadcastText = (text: string, speakNow = false) =>
  envelope('broadcast_text', { text, speakNow });
export const makeUserText = (text: string) => envelope('user_text', { text });
export const makeSwitchLlm = (p: CmdPayloadMap['switch_llm']) => envelope('switch_llm', p);
export const makeSwitchVoice = (p: TtsVoicePayload) => envelope('switch_voice', p);
export const makeInterrupt = () => envelope('interrupt', {});
export const makeInjectHistory = (role: 'user' | 'assistant', text: string) =>
  envelope('inject_history', { role, text });

/**
 * @brief 运行时校验收到的 JSON 是不是合法 Cmd
 */
export const parseCmd = (raw: unknown): CmdEnvelope | null => {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.type !== 'string' || typeof r.id !== 'string' || typeof r.ts !== 'number') {
    return null;
  }
  if (!r.payload || typeof r.payload !== 'object') return null;

  const type = r.type as CmdType;
  const p = r.payload as Record<string, unknown>;

  switch (type) {
    case 'set_mode':
      if (p.mode !== 'doubao-llm' && p.mode !== 'external-llm' && p.mode !== 'text-broadcast') {
        return null;
      }
      break;
    case 'broadcast_text':
      if (typeof p.text !== 'string') return null;
      break;
    case 'user_text':
      if (typeof p.text !== 'string') return null;
      break;
    case 'switch_llm':
      if (typeof p.provider !== 'string' || typeof p.model !== 'string') return null;
      break;
    case 'switch_voice':
      if (typeof p.voice_type !== 'string') return null;
      break;
    case 'interrupt':
      break;
    case 'inject_history':
      if ((p.role !== 'user' && p.role !== 'assistant') || typeof p.text !== 'string') return null;
      break;
    default:
      return null;
  }

  return raw as CmdEnvelope;
};
